import dayjs from 'dayjs';
import { getFormattedName, getInitialsFromName } from '../../utility/general-utility.js';
import { MapUtils } from '../../utility/map-utils.js';
import { VersionRange } from '../core/version-range.js';
import { DataModel, DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../dto/dto.js';
import { PhoneNumber } from './phone-number.js';
import { PhotoReference } from './file-reference/photo-reference.js';
import { ComponentList } from '../../components/core/component-list.js';
import { Component } from '../../components/core/component.js';
import { TimeZone } from '../../time/timezone.js';
import { CustomerAccountBase } from './customer-account-base.js';

export class CustomerAccount extends CustomerAccountBase {
  static readonly TYPE_ID: string = '12807bac2fb34d99b7924b5413db42a7';
  static readonly SCHEMA_VERSION: number = 5;

  public readonly formattedName: string;
  public readonly initials: string;

  constructor(
    public readonly uniqueId: string,
    public readonly firstName: string,
    public readonly lastName: string,
    public readonly email: string,
    public readonly phoneNumber: PhoneNumber,
    public readonly photo: PhotoReference | null,
    public readonly pushNotificationToken: string | null,
    public readonly creationDate: dayjs.Dayjs | null,
    public readonly components: ComponentList,
  ) {
    super();
    this.formattedName = getFormattedName(firstName, lastName)!;
    this.initials = getInitialsFromName(firstName, lastName)!;
  }

  get $typeId() {
    return CustomerAccount.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(CustomerAccount.SCHEMA_VERSION);
  }

  get hasAName(): boolean {
    return (this.firstName !== null && this.firstName.length !== 0) ||
          (this.lastName !== null && this.lastName.length !== 0);
  }

  static fromMap(context: SerializationContext, map: SerializedData): CustomerAccount {
    return new CustomerAccount(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.get<string>(map, 'firstName'),
        MapUtils.get<string>(map, 'lastName'),
        MapUtils.get<string>(map, 'email'),
        PhoneNumber.mustDecode(MapUtils.get<string>(map, 'phoneNumber')),
        context.fromDataOrNull<PhotoReference>(MapUtils.getRaw(map, 'photo') as SerializedData),
        MapUtils.getOrNull<string>(map, 'pushNotificationToken'),
        context.decodeDateOrNull(MapUtils.getOrNull<number>(map, 'creationDate')),
        ComponentList.deserialize(
            context, MapUtils.getList<SerializedData>(map, 'components', (data) => data as SerializedData),
        ),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'firstName': this.firstName,
      'lastName': this.lastName,
      'email': this.email,
      'phoneNumber': this.phoneNumber.encode(),
      'photo': context.toDataOrNull(this.photo),
      'pushNotificationToken': this.pushNotificationToken,
      'creationDate': context.encodeDateOrNull(this.creationDate),
      'components': this.components.serialize(context),
    };
  }

  clone(): this {
    return new CustomerAccount(
        this.uniqueId,
        this.firstName,
        this.lastName,
        this.email,
        this.phoneNumber.clone(),
        this.photo?.clone() ?? null,
        this.pushNotificationToken,
        TimeZone.cloneDateOrNull(this.creationDate),
        this.components.clone(),
    ) as this;
  }

  isSame(other: CustomerAccount): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.firstName === this.firstName &&
        other.lastName === this.lastName &&
        other.email === this.email &&
        isSameData(other.phoneNumber, this.phoneNumber) &&
        isSameData(other.photo, this.photo) &&
        other.pushNotificationToken === this.pushNotificationToken &&
        other.creationDate === this.creationDate &&
        other.components.isSame(this.components);
  }

  copyWith(fields?: {
      uniqueId?: string,
      firstName?: string,
      lastName?: string,
      email?: string,
      phoneNumber?: PhoneNumber,
      photo?: PhotoReference,
      pushNotificationToken?: string | null,
      creationDate?: dayjs.Dayjs,
      components?: ComponentList,
    }): CustomerAccount {
    return new CustomerAccount(
        fields?.uniqueId ?? this.uniqueId,
        fields?.firstName ?? this.firstName,
        fields?.lastName ?? this.lastName,
        fields?.email ?? this.email,
        fields?.phoneNumber ?? this.phoneNumber,
        fields?.photo ?? this.photo,
        fields?.pushNotificationToken == null ?
          null :
          fields?.pushNotificationToken ?? this.pushNotificationToken,
        fields?.creationDate ?? (this.creationDate?.clone() ?? null),
        fields?.components ?? this.components.clone(),
    );
  }

  hasComponentByTypeId(typeId: string): boolean {
    return this.components.hasComponentByTypeId(typeId);
  }

  mustFindComponentByTypeId<C extends Component>(typeId: string): C {
    return this.components.mustFindComponentByTypeId<C>(typeId);
  }

  findComponentByTypeId<C extends Component>(typeId: string): C | null {
    return this.components.findComponentByTypeId<C>(typeId);
  }

  findComponentByInstanceId<C extends Component>(instanceId: string): C | null {
    return this.components.findComponentByInstanceId<C>(instanceId);
  }

  findComponentsByTypeId<C extends Component>(typeId: string): C[] {
    return this.components.findComponentsByTypeId<C>(typeId);
  }

  addComponent<C extends Component>(component: C): void {
    this.components.addComponent(component);
  }

  addComponents(list: Component[]): void {
    this.components.addComponents(list);
  }

  removeComponentByTypeId(typeId: string): boolean {
    return this.components.removeComponentByTypeId(typeId);
  }

  removeComponentByInstanceId(instanceId: string): boolean {
    return this.components.removeComponentByInstanceId(instanceId);
  }

  removeComponentAt(index: number): void {
    this.components.removeComponentAt(index);
  }

  removeAllComponents(): void {
    this.components.removeAllComponents();
  }

  popAllComponentsByTypeId<C extends Component>(typeId: string): C[] {
    return this.components.popAllComponentsByTypeId(typeId);
  }

  popComponentByTypeId<C extends Component>(typeId: string): C | null {
    return this.components.popComponentByTypeId(typeId);
  }

  popComponent<C extends Component>(component: C): C | null {
    return this.components.popComponent(component);
  }

  replaceComponent<C extends Component>(replacement: C): boolean {
    return this.components.replaceComponent(replacement);
  }

  addOrReplaceComponent<C extends Component>(component: C): void {
    this.components.addOrReplaceComponent(component);
  }

  mustFindComponentField<C extends Component, F>(componentTypeId: string, find: (c: C) => F): F {
    return this.components.mustFindComponentField<C, F>(componentTypeId, find);
  }

  findComponentField<C extends Component, F>(componentTypeId: string, find: (c: C) => F): F | null {
    return this.components.findComponentField<C, F>(componentTypeId, find);
  }

  static verifyType(dm: DataModel): boolean {
    return dm instanceof CustomerAccount;
  }

  static assertType(dm: DataModel): dm is CustomerAccount {
    return dm instanceof CustomerAccount;
  }
}

export class CustomerAccountBuilder extends DataModelBuilder<CustomerAccount> {
  build(context: SerializationContext, map: SerializedData): CustomerAccount {
    return CustomerAccount.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(CustomerAccount.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(CustomerAccount.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    /**
     * In version 4, we added the `components` field.
     */
    if(version < 4) {
      version = 4;
    }

    /**
     * In version 5, we added moved the `stripeCustomerId` field to the billing details component.
     */
    if(version < 5) {
      version = 5;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
